import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  getAirshipActiveMissions,
  getAirshipCities,
  getAirshipStatus,
  launchAirship,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function AirshipPage() {
  const [toCityId, setToCityId] = useState<number | "">("")
  const { syncGameState } = useGuardiansPassport()

  const statusQuery = useQuery({
    queryKey: ["airship-status"],
    queryFn: getAirshipStatus,
  })

  const citiesQuery = useQuery({
    queryKey: ["airship-cities"],
    queryFn: getAirshipCities,
  })

  const missionsQuery = useQuery({
    queryKey: ["airship-missions"],
    queryFn: getAirshipActiveMissions,
    refetchInterval: 15_000,
  })

  const launch = useMutation({
    mutationFn: () => {
      if (toCityId === "") throw new Error("Выберите город")
      return launchAirship({ toCityId: Number(toCityId) })
    },
    onSuccess: async () => {
      await syncGameState()
      void statusQuery.refetch()
      void missionsQuery.refetch()
    },
  })

  const status = statusQuery.data

  return (
    <GameScreenLayout title="Дирижабль" backTo="/play/map">
      {statusQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : status ? (
        <div className="space-y-4 text-sm">
          <section className="rounded-xl border border-white/10 p-4">
            <p>{status.hasHangar ? "Ангар есть" : "Нет ангара"}</p>
            {status.airship ? (
              <p className="text-guardians-muted">
                Разведка {status.airship.scoutLevel} · груз {status.cargoCount}
              </p>
            ) : (
              <p className="text-guardians-muted">
                Покупка: {status.purchaseCoins} 🪙
              </p>
            )}
            {status.activeMission ? (
              <p className="mt-2 text-xs text-guardians-accent">
                Рейс в город {status.activeMission.toCityId} · {status.activeMission.status}
              </p>
            ) : null}
          </section>

          {status.canLaunch && status.airship ? (
            <section className="space-y-2">
              <label className="block text-xs text-guardians-muted">
                Куда летим
                <select
                  className="mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2"
                  value={toCityId}
                  onChange={(e) =>
                    setToCityId(e.target.value ? Number(e.target.value) : "")
                  }
                >
                  <option value="">Город</option>
                  {(citiesQuery.data ?? []).map((c) => (
                    <option key={c.cityId} value={c.cityId}>
                      {c.title} ({c.scannablePoints} точек)
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                disabled={launch.isPending || toCityId === ""}
                onClick={() => launch.mutate()}
                className="w-full rounded-xl bg-guardians-accent py-2 text-guardians-bg disabled:opacity-40"
              >
                Запуск ({status.tripFeeCoins} 🪙)
              </button>
            </section>
          ) : null}

          {(missionsQuery.data ?? []).length ? (
            <section>
              <h3 className="mb-2 font-medium">Миссии на карте</h3>
              <ul className="space-y-1 text-xs text-guardians-muted">
                {missionsQuery.data!.map((m) => (
                  <li key={m.id}>
                    {m.id.slice(0, 8)}… · {m.status ?? "—"}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}

      {launch.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(launch.error, "Запуск не удался")}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
